import { apiRequest } from './authService';

/**
 * Get all wallets for a customer
 * @param {string|number} customerId Customer ID
 * @returns {Promise<{success: boolean, data?: array, message?: string}>}
 */
export async function getCustomerWallets(customerId) {
  try {
    const response = await apiRequest('wallets/customer', {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId })
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        data: [],
        message: response.response || 'Failed to fetch wallets'
      };
    }
  } catch (error) {
    console.error('Get customer wallets error:', error);
    return {
      success: false,
      data: [],
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Get a specific wallet by customer ID and currency
 * @param {string|number} customerId Customer ID
 * @param {string} currency Currency code (USD, EUR, SRD)
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function getWallet(customerId, currency) {
  try {
    const response = await apiRequest('wallets/get', {
      method: 'POST',
      body: JSON.stringify({ 
        customer_id: customerId,
        currency: currency
      })
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        message: response.response || 'Wallet not found'
      };
    }
  } catch (error) {
    console.error('Get wallet error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Set wallet balance (create or update)
 * @param {string|number} customerId Customer ID
 * @param {string} currency Currency code (USD, EUR, SRD)
 * @param {number} amount Amount to set
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function setWallet(customerId, currency, amount) {
  try {
    const response = await apiRequest('wallets/set', {
      method: 'POST',
      body: JSON.stringify({ 
        customer_id: customerId,
        currency: currency,
        amount: amount
      })
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        message: response.response || 'Failed to set wallet'
      };
    }
  } catch (error) {
    console.error('Set wallet error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Adjust wallet balance (add or subtract)
 * @param {string|number} customerId Customer ID
 * @param {string} currency Currency code (USD, EUR, SRD)
 * @param {number} amount Amount to add (positive) or subtract (negative)
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function adjustWallet(customerId, currency, amount) {
  try {
    const response = await apiRequest('wallets/adjust', {
      method: 'POST',
      body: JSON.stringify({ 
        customer_id: customerId,
        currency: currency,
        amount: amount
      })
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        message: response.response || 'Failed to adjust wallet'
      };
    }
  } catch (error) {
    console.error('Adjust wallet error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Initialize wallets for a customer (creates all three currency wallets)
 * @param {string|number} customerId Customer ID
 * @returns {Promise<{success: boolean, data?: array, message?: string}>}
 */
export async function initializeWallets(customerId) {
  try {
    const response = await apiRequest('wallets/initialize', {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId })
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        data: [],
        message: response.response || 'Failed to initialize wallets'
      };
    }
  } catch (error) {
    console.error('Initialize wallets error:', error);
    return {
      success: false,
      data: [],
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Make a deposit to a wallet
 * @param {string|number} customerId Customer ID
 * @param {string} currency Currency code (USD, EUR, SRD)
 * @param {number} amount Amount to deposit (always positive)
 * @param {string} description Transaction description
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function deposit(customerId, currency, amount, description = 'Deposit') {
  try {
    const response = await apiRequest('wallets/deposit', {
      method: 'POST',
      body: JSON.stringify({ 
        customer_id: customerId,
        currency: currency,
        amount: Math.abs(amount), // Ensure positive
        description: description
      })
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        message: response.response || 'Failed to deposit'
      };
    }
  } catch (error) {
    console.error('Deposit error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Make a withdrawal from a wallet
 * @param {string|number} customerId Customer ID
 * @param {string} currency Currency code (USD, EUR, SRD)
 * @param {number} amount Amount to withdraw (always positive)
 * @param {string} description Transaction description
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function withdrawal(customerId, currency, amount, description = 'Withdrawal') {
  try {
    const response = await apiRequest('wallets/withdrawal', {
      method: 'POST',
      body: JSON.stringify({ 
        customer_id: customerId,
        currency: currency,
        amount: Math.abs(amount), // Ensure positive (API will make it negative)
        description: description
      })
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        message: response.response || 'Failed to withdraw'
      };
    }
  } catch (error) {
    console.error('Withdrawal error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Search transactions for a customer
 * @param {string|number} customerId Customer ID
 * @param {string} searchQuery Search query (searches description, type, amount)
 * @param {string} currency Optional: Filter by currency
 * @param {number} limit Optional: Limit number of results
 * @returns {Promise<{success: boolean, data?: array, message?: string}>}
 */
export async function searchTransactions(customerId, searchQuery, currency = null, limit = 100) {
  try {
    const body = { customer_id: customerId, query: searchQuery, limit: limit };
    if (currency) body.currency = currency;

    const response = await apiRequest('transactions/search', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        data: [],
        message: response.response || 'Failed to search transactions'
      };
    }
  } catch (error) {
    console.error('Search transactions error:', error);
    return {
      success: false,
      data: [],
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Get transaction history for a customer
 * @param {string|number} customerId Customer ID
 * @param {string} currency Optional: Filter by currency
 * @param {number} limit Optional: Limit number of results
 * @returns {Promise<{success: boolean, data?: array, message?: string}>}
 */
export async function getCustomerTransactions(customerId, currency = null, limit = 100) {
  try {
    const body = { customer_id: customerId, limit: limit };
    if (currency) body.currency = currency;

    const response = await apiRequest('transactions/customer', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        data: [],
        message: response.response || 'Failed to fetch transactions'
      };
    }
  } catch (error) {
    console.error('Get transactions error:', error);
    return {
      success: false,
      data: [],
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Delete a transaction and reverse its effect on wallet balance
 * @param {string|number} transactionId Transaction ID
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function deleteTransaction(transactionId) {
  try {
    const response = await apiRequest('transactions/delete', {
      method: 'POST',
      body: JSON.stringify({ transaction_id: transactionId })
    });

    if (response.success) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        message: response.response || 'Failed to delete transaction'
      };
    }
  } catch (error) {
    console.error('Delete transaction error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Edit a transaction (amount, type, description). created_at is preserved.
 * Wallet balance and subsequent transactions are automatically adjusted by the API.
 * @param {string|number} transactionId
 * @param {{amount: number, type: string, description?: string}} payload
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function editTransaction(transactionId, payload) {
  try {
    const body = {
      transaction_id: transactionId,
      amount: payload.amount,
      type: payload.type,
      description: payload.description || ''
    };

    const response = await apiRequest('transactions/edit', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to edit transaction' };
  } catch (error) {
    console.error('Edit transaction error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Fetch the edit audit log for a single transaction.
 * @param {string|number} transactionId
 * @returns {Promise<{success: boolean, data?: array, message?: string}>}
 */
export async function getTransactionEdits(transactionId) {
  try {
    const response = await apiRequest('transactions/edits', {
      method: 'POST',
      body: JSON.stringify({ transaction_id: transactionId })
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to fetch edit history' };
  } catch (error) {
    console.error('Get transaction edits error:', error);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

/**
 * Get global transaction history with optional filters.
 * @param {Object} filters
 * @param {number} [filters.date_from] Unix seconds
 * @param {number} [filters.date_to]   Unix seconds
 * @param {string|number} [filters.customer_id]
 * @param {string} [filters.currency] USD/EUR/SRD
 * @param {string} [filters.type] deposit/withdrawal/adjustment/transfer
 * @param {number} [filters.limit] default 500
 * @returns {Promise<{success: boolean, data?: {transactions: Array, totals: Object, count: number}, message?: string}>}
 */
export async function getTransactionsHistory(filters = {}) {
  try {
    const body = {};
    if (filters.date_from !== undefined && filters.date_from !== null && filters.date_from !== '') {
      body.date_from = filters.date_from;
    }
    if (filters.date_to !== undefined && filters.date_to !== null && filters.date_to !== '') {
      body.date_to = filters.date_to;
    }
    if (filters.customer_id) body.customer_id = filters.customer_id;
    if (filters.currency) body.currency = filters.currency;
    if (filters.type) body.type = filters.type;
    if (filters.limit) body.limit = filters.limit;

    const response = await apiRequest('transactions/history', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        message: response.response || 'Failed to fetch transaction history'
      };
    }
  } catch (error) {
    console.error('Get transactions history error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Get full transaction history for a customer for PDF export
 * Returns customer info, current balances, and all transactions (oldest -> newest)
 * @param {string|number} customerId Customer ID
 * @param {string} currency Optional: Filter by currency
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function getTransactionsExport(customerId, currency = null) {
  try {
    const body = { customer_id: customerId };
    if (currency) body.currency = currency;

    const response = await apiRequest('transactions/export', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        message: response.response || 'Failed to fetch export data'
      };
    }
  } catch (error) {
    console.error('Get transactions export error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Get total balances for all clients (sum of all customer wallets)
 * @returns {Promise<{success: boolean, data?: {USD: number, EUR: number, SRD: number}, message?: string}>}
 */
export async function getAllClientsBalance() {
  try {
    const response = await apiRequest('wallets/all-clients-balance', {
      method: 'POST'
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.response
      };
    } else {
      return {
        success: false,
        data: { USD: 0, EUR: 0, SRD: 0 },
        message: response.response || 'Failed to fetch total balances'
      };
    }
  } catch (error) {
    console.error('Get all clients balance error:', error);
    return {
      success: false,
      data: { USD: 0, EUR: 0, SRD: 0 },
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Per-client balances for all active customers (one query, same source as
 * getAllClientsBalance so the totals match).
 * @returns {Promise<{success: boolean, data?: {clients: Array, totals: object}, message?: string}>}
 */
export async function getAllClientsSaldos() {
  try {
    const response = await apiRequest('wallets/all-clients-saldos', { method: 'POST' });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to fetch client saldos' };
  } catch (error) {
    console.error('Get all clients saldos error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Helper function to get wallet balances as an object keyed by currency
 * @param {string|number} customerId Customer ID
 * @returns {Promise<{USD: number, EUR: number, SRD: number}>}
 */
export async function getWalletBalances(customerId) {
  const result = await getCustomerWallets(customerId);
  
  const balances = { USD: 0, EUR: 0, SRD: 0 };
  
  if (result.success && result.data) {
    result.data.forEach(wallet => {
      balances[wallet.currency] = parseFloat(wallet.amount) || 0;
    });
  }
  
  return balances;
}
